import React, {useContext, Fragment} from 'react';
import {FormattedMessage} from 'react-intl';

import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';
import {Checkbox} from '@homeaway/react-form-components';

import {
    getFiltersWithSelection,
    selectCheckboxes,
    unselectCheckboxes,
    updateSearchFilters,
    getCheckboxLineClassName,
} from '../../utils/freeCancellationUtils';

import {triggerEdapEventForCheckbox} from '../../utils/edap';
import {FREE_CANCELLATION_GROUP_ID} from '../../constants/filters';

import type {FilterGroupContentProps} from '../FilterGroupContent/FilterGroupContent';

import './FreeCancellationFilter.less';

type Props = Pick<
    FilterGroupContentProps,
    'filters' | 'selectedFilters' | 'onMultipleFilterToggle'
> & {
    className?: string;
    rank: number;
};

export default function FreeCancellationFilter({
    filters,
    selectedFilters,
    onMultipleFilterToggle,
    rank,
}: Props) {
    const filtersCheckboxes = getFiltersWithSelection(filters, selectedFilters);
    const edapContext: TrackingContextValue = useContext(TrackingContext);

    const handleChecked = (event) => {
        let updatedFiltersCheckboxes;
        const {checked, name: filterId} = event.currentTarget;

        if (checked) {
            updatedFiltersCheckboxes = selectCheckboxes(
                filtersCheckboxes,
                filterId
            );
        } else {
            updatedFiltersCheckboxes = unselectCheckboxes(
                filtersCheckboxes,
                filterId
            );
        }

        triggerEdapEventForCheckbox(
            event,
            FREE_CANCELLATION_GROUP_ID,
            rank,
            edapContext
        );

        updateSearchFilters(updatedFiltersCheckboxes, onMultipleFilterToggle);
    };

    return (
        <div
            role="menuitem"
            className="FreeCancellationFilter"
            data-wdio="FreeCancellationFilter"
        >
            <div className="FreeCancellationFilter__secondaryLabel">
                <FormattedMessage
                    defaultMessage="Think your plans might change? No worries. Filter for places that let you cancel for free up to:"
                    description="Information label for free cancellation dropdown"
                    id="freeCancellationSecondaryLabelV2"
                />
            </div>
            <div className="FreeCancellationFilter__checkboxes">
                {filtersCheckboxes.map((filter, index) => {
                    const {isChecked, isDisabled, label, filterId} = filter;
                    const checkboxLineClassName = getCheckboxLineClassName(
                        filtersCheckboxes,
                        index,
                        isChecked,
                        isDisabled
                    );

                    return (
                        <Fragment key={index}>
                            <Checkbox
                                checked={!isDisabled && isChecked}
                                label={label}
                                name={filterId}
                                onChange={handleChecked}
                                disabled={isDisabled}
                                id={filterId}
                                data-groupid={FREE_CANCELLATION_GROUP_ID}
                            />
                            <div className={checkboxLineClassName} />
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
}
